import { useRef, useState, type ReactElement, type ReactNode } from "react";
import { Button, PersistentNotice, ProgressBar, centreOffset, logical, useCentred, useLogicalSize, type Centred, type GlyphName } from "../ui/kit/index.ts";
import { say } from "../words.ts";
import { actionWords } from "./disconnect.ts";
import { WORDMARK } from "./entry-screen.tsx";
import { LOADING_METRICS, loadingPlaces } from "./loading-layout.ts";
import { pickLoadingTip } from "./loading-tips.ts";
import { loadingView, type LoadingFailure, type LoadingState, type LoadingStepId } from "./loading-state.ts";
import { QueueScreen } from "./queue/queue-window.tsx";

/**
 * The loading screen (P1.4-T5, ADR-061 and its amendments; the design update's shared full-screen loading
 * layout is a visual reference only). `LoadingScreen` is the layout every full-screen load can share: an art
 * band across the top, under it a column holding the bar block (the progress bar with the step on its line
 * under the track and the count of finished steps at the line's end, as the design draws it), and in place of the bar block a persistent notice when the
 * load fails, with no spinner anywhere. A later full-screen load adds its lines above the bar block and its
 * own art without a rewrite. `GameStartScreen` fills it for game start: the wordmark in the art band, since
 * there is no key art and the title screen's wordmark is the game's name (the brief's title screen), the
 * shell's steps, and a failure's notice with Retry, or, once the page has left a world it showed, the
 * disconnect notice with its action (P1.9-T15). Under the bar it shows one tip, a different one each load
 * (P1.9-T217, `loading-tips.ts`), and there is no title under the band ("Aethervale" waits on the author,
 * ADR-064's open question 5). While the page's `hello` waits for room, game start shows the queue window in the bar's place (P1.4-T164, `queue/queue-window.tsx`).
 *
 * Sizes are whole logical pixels: the band, the column, the bar and the notice from the design update, the
 * bar's and the notice's own parts from the kit's metrics, and the wordmark the title face at its measured
 * size. The band, the column and the notice are set in the middle across on whole logical pixels by
 * measurement (`useCentred`), since the screen can be a fraction of a logical pixel wide, and the wordmark in
 * the middle of the band both ways. On an area shorter than the design's the band gives way to the bar block and
 * the notice rises, so both are whole on the page (`loadingPlaces`; ADR-061's amendment of 2026-10-06).
 */

export { LOADING_METRICS };

/**
 * The bar block's content: the step on the bar's line, the count after it, and the bar's value over its
 * maximum.
 */
export interface LoadingBar {
  readonly label: string;
  readonly count: string;
  readonly value: number;
  readonly max: number;
}

/**
 * What a full-screen load shows: its art, and either its bar block or the notice of its failure.
 */
export interface LoadingScreenProps {
  readonly art: ReactNode;
  readonly bar: LoadingBar;
  readonly notice: ReactNode;
  readonly tip?: string | null;
}

/**
 * A centred part's visibility: hidden until `useCentred` has placed it, so it never shows for a frame at the top
 * left before it centres (G16-3: the shell's host is hidden while character select stands in front, and shown
 * again after Enter the world by a resize the observer reports only after a paint). The wordmark and the bar block
 * take it; the notice does not, since it takes focus as it appears and a hidden control cannot, and it only ever
 * appears in a shown host, where it is placed before the first paint.
 *
 * @param place The part's place.
 * @returns `hidden` until it is placed, then undefined, leaving the stylesheet's.
 */
function shownOnce(place: Centred): "hidden" | undefined {
  return place.placed ? undefined : "hidden";
}

/**
 * The shared full-screen loading layout. While a notice shows, it stands in place of the bar block and the
 * art band is left empty, since the notice sits over the band's middle.
 *
 * @param props The art, the bar block, the notice (null, undefined or false when there is none) and the tip line (none when absent or null).
 * @returns The screen.
 */
export function LoadingScreen({ art, bar, notice, tip }: LoadingScreenProps): ReactElement {
  const screen = useRef<HTMLDivElement>(null);
  const band = useRef<HTMLDivElement>(null);
  const artBox = useRef<HTMLDivElement>(null);
  const column = useRef<HTMLDivElement>(null);
  const noticeBox = useRef<HTMLDivElement>(null);
  const failing = notice !== null && notice !== undefined && notice !== false;
  const artAt = useCentred(band, artBox, { x: true, y: true }, failing);
  const columnAt = useCentred(screen, column, { x: true, y: false }, failing);
  const noticeAt = useCentred(screen, noticeBox, { x: true, y: false }, failing);
  const places = loadingPlaces(useLogicalSize(screen, failing)?.height ?? null, useLogicalSize(column, failing)?.height ?? null, useLogicalSize(noticeBox, failing)?.height ?? null);
  return (
    <div ref={screen} className="ot-loading" data-loading="screen" data-loading-failing={failing ? "true" : "false"}>
      <div ref={band} className="ot-loading__art" data-loading="art" style={{ height: logical(places.art), borderBottomWidth: logical(LOADING_METRICS.artLine) }}>
        {failing ? null : (
          <div ref={artBox} className="ot-loading__art-content" style={{ left: logical(artAt.left), top: logical(artAt.top), visibility: shownOnce(artAt) }}>
            {art}
          </div>
        )}
      </div>
      <div ref={column} className="ot-loading__column" data-loading="column" style={{ left: logical(columnAt.left), top: logical(places.columnTop), width: logical(LOADING_METRICS.column.width), visibility: shownOnce(columnAt) }}>
        {failing ? null : (
          <div className="ot-loading__bar" data-loading="bar" style={{ marginLeft: logical(centreOffset(LOADING_METRICS.column.width, LOADING_METRICS.bar)) }}>
            <ProgressBar label={bar.label} count={bar.count} value={bar.value} max={bar.max} width={LOADING_METRICS.bar} />
          </div>
        )}
        {failing || tip === undefined || tip === null ? null : (
          <p className="ot-loading__tip" data-loading="tip" style={{ marginLeft: logical(centreOffset(LOADING_METRICS.column.width, LOADING_METRICS.bar)), width: logical(LOADING_METRICS.bar) }}>
            {tip}
          </p>
        )}
        {failing ? null : (
          <p className="ot-loading__early" data-loading="early" style={{ marginLeft: logical(centreOffset(LOADING_METRICS.column.width, LOADING_METRICS.bar)), width: logical(LOADING_METRICS.bar) }}>
            {say("loading.earlyDevelopment")}
          </p>
        )}
      </div>
      {failing ? (
        <div ref={noticeBox} className="ot-loading__notice" data-loading="notice" style={{ left: logical(noticeAt.left), top: logical(places.noticeTop), width: logical(LOADING_METRICS.notice.width) }}>
          {notice}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The game's wordmark in the title face at its measured size, as the art band shows it at game start.
 *
 * @returns The wordmark.
 */
export function Wordmark(): ReactElement {
  return (
    <p className="ot-loading__wordmark" style={{ fontSize: logical(LOADING_METRICS.wordmark.size), lineHeight: logical(LOADING_METRICS.wordmark.lineHeight) }}>
      {WORDMARK}
    </p>
  );
}

/**
 * The bar block for a state: the step the page waits on, or the last step once all are finished, and the
 * count of finished steps.
 *
 * @param state The shell's state.
 * @returns The bar block's content.
 */
export function barOf(state: LoadingState): LoadingBar {
  const view = loadingView(state);
  const step = view.step ?? (state.steps.at(-1)?.id as LoadingStepId);
  return { label: stepWords(step), count: view.count, value: view.value, max: view.max };
}

/**
 * A loading step's name on the bar.
 *
 * @param step The step.
 * @returns Its words, "Atlas pages".
 */
export function stepWords(step: LoadingStepId): string {
  return step === "sign-in" ? say("loading.steps.signIn") : say(`loading.steps.${step}`);
}

/**
 * The label of a notice's button: Retry for a failure before the reveal, or the disconnect notice's action
 * (P1.9-T15).
 *
 * @param action What the button does.
 * @returns The label.
 */
export function actionLabel(action: LoadingFailure["action"]): string {
  return action === "retry" ? say("loading.retry") : actionWords(action);
}

/**
 * The glyph a notice's button leads with (ADR-061's amendment "the kit and the entry forms match the design"): the
 * reload glyph on each action that tries again (Retry, Reconnect and Reload), and none on Log in, which leaves.
 *
 * @param action What the button does.
 * @returns The glyph, or undefined for none.
 */
export function actionGlyph(action: LoadingFailure["action"]): GlyphName | undefined {
  return action === "log-in" ? undefined : "reload";
}

/**
 * Which notice a failure shows: a failure that replaces another with other words or another action is another
 * notice, so it mounts afresh and takes focus to its own action, as a notice does as it appears (P1.9-T15: a
 * disconnect notice that gives way to a sign-in that ended, with Log in in place of Reconnect).
 *
 * @param failure The failure.
 * @returns A key naming it.
 */
export function noticeKey(failure: LoadingFailure): string {
  return JSON.stringify([failure.category, failure.action, failure.title, failure.sentence, failure.detail]);
}

/**
 * Does nothing, for a queue window drawn with no page behind it.
 */
function nothing(): void {
  return undefined;
}

/**
 * A notice button's `icon` prop, left out when its action has no glyph.
 *
 * @param action What the button does.
 * @returns The prop, or none.
 */
function iconOf(action: LoadingFailure["action"]): { readonly icon?: GlyphName } {
  const glyph = actionGlyph(action);
  return glyph === undefined ? {} : { icon: glyph };
}

/**
 * What game start needs for the queue window: the character's name as the page knows it, empty when it does not,
 * and what Leave queue does.
 */
export interface QueueHost {
  readonly name: () => string;
  readonly onLeave: () => void;
}

/**
 * Game start: the wordmark over the shell's progress, or the failure's notice with its button: Retry before
 * the reveal, and after it the disconnect notice's Reconnect, Reload or Log in (P1.9-T15). While the shell holds a
 * place in the login queue and shows no notice, the queue window stands in the whole screen's place (P1.4-T164).
 *
 * @param props The shell's state, what the notice's button does, and what the queue window needs; without the
 *   last the window shows no name and its Leave queue does nothing, as on a page that only draws the screen.
 * @returns The screen.
 */
export function GameStartScreen({ state, onRetry, queue }: { readonly state: LoadingState; readonly onRetry: () => void; readonly queue?: QueueHost }): ReactElement {
  const [tip] = useState(() => pickLoadingTip());
  const failure = state.failure;
  const step = loadingView(state).step;
  if (failure === null && state.queue !== null) {
    return (
      <div className="ot-loading__host" data-loading-step={step ?? ""} data-loading-category="" data-loading-action="" data-loading-queue="true">
        <QueueScreen queue={state.queue} scheduled={state.scheduled} name={queue?.name() ?? ""} onLeave={queue?.onLeave ?? nothing} />
      </div>
    );
  }
  return (
    <div className="ot-loading__host" data-loading-step={step ?? ""} data-loading-category={failure?.category ?? ""} data-loading-action={failure?.action ?? ""}>
      <LoadingScreen
        art={<Wordmark />}
        bar={barOf(state)}
        tip={tip}
        notice={
          failure === null ? null : (
            <PersistentNotice
              key={noticeKey(failure)}
              title={failure.title}
              sentence={failure.sentence}
              {...(failure.detail === "" ? {} : { detail: failure.detail })}
              width={LOADING_METRICS.notice.width}
              actions={
                <Button variant="primary" onClick={onRetry} {...iconOf(failure.action)}>
                  {actionLabel(failure.action)}
                </Button>
              }
            />
          )
        }
      />
    </div>
  );
}
